/**
 * ApartmentSync Loader Block
 *
 * Site-global injection block that loads the ApartmentSync (Repli360 / RRAC)
 * script once per page. The script handles widget rendering, site-wide phone
 * number replacement, and call tracking — concerns that are global to the
 * site rather than tied to a specific floorplan or map widget.
 *
 * The apartment-sync (floorplans) and apartment-sync-map (interactive map)
 * section blocks hook into this loader when present and auto-fall-back to
 * self-loading when it is not, so existing pages continue to work.
 *
 * All loading logic lives in useRRACScript — this block just calls it with
 * role: 'owner' and returns null.
 */

import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useRRACScript } from '../../hooks/useRRACScript';

function ApartmentSyncLoaderComponent(_props: BlockProps) {
  const javascriptId = useBlockToken('javascript_id', {
    defaultField: 'custom_apartmentsync_javascript_id',
  });
  const configId = useBlockToken('config_id', {
    defaultField: 'custom_apartmentsync_config_id',
  });
  const injectDniClassesRaw = useBlockToken('inject_dni_classes', {
    defaultValue: 'false',
  });

  useRRACScript({
    javascriptId: javascriptId || null,
    configId: configId || null,
    role: 'owner',
    injectDniClasses: injectDniClassesRaw !== 'false',
  });

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncLoader',
  displayName: 'ApartmentSync Loader',
  tokens: {
    javascript_id: {
      label: 'JavaScript ID',
      type: 'text',
      defaultField: 'custom_apartmentsync_javascript_id',
      suggestedFields: ['custom_apartmentsync_javascript_id'],
      description:
        'Encoded token for the ApartmentSync script URL. Found in the Repli360 admin dashboard.',
      required: true,
    },
    config_id: {
      label: 'Config ID (Site ID)',
      type: 'text',
      defaultField: 'custom_apartmentsync_config_id',
      suggestedFields: ['custom_apartmentsync_config_id'],
      description:
        'ApartmentSync site identifier. Baked into the loaded script; stored here for reference.',
    },
    inject_dni_classes: {
      label: 'Inject DNI Classes on Phone Links',
      type: 'text',
      defaultValue: 'false',
      description:
        'Set to "true" to replace every tel:/sms: anchor on the page so the ApartmentSync script can swap in the call-tracking number site-wide.',
    },
  },
};

export const apartmentSyncLoaderBlock: BlockDefinition = {
  id: 'apartment-sync-loader',
  name: 'ApartmentSync Loader',
  description:
    'Site-wide loader for the ApartmentSync (Repli360) script. Enables phone-number replacement and call tracking on every page, and lets the floorplan / interactive-map blocks share a single script load.',
  category: 'Integrations',
  placement: 'injection',
  component: ApartmentSyncLoaderComponent,
  contentSchema,
  tags: ['apartmentsync', 'repli360', 'rrac', 'tracking', 'phone', 'global', 'loader'],
};
